// Helpers + componentes de captura compartidos entre L2 (captura), L7
// (historial) y la edición de registros (EditarHoraDialog). Extraídos de
// L2CapturaPage para reuso sin duplicar lógica de formateo ni el input de
// cantidades con separador de miles.

import { useState } from 'react';
import type { BloqueTurno, UnidadMedida } from 'schemas';
import { bloqueQueContieneHora } from 'schemas';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { semaforoCumplimiento, toneTextClass } from '../../lib/semaforo';

export function formatearMiles(n: number): string {
  return new Intl.NumberFormat('es-CO').format(n);
}

/**
 * Detalle del aviso «Registro guardado». No dice «unidades»: hay SKUs que se
 * miden en kg (producto a granel) y la etiqueta del SKU ya trae su unidad.
 */
export function detalleRegistroGuardado(skuLabel: string, cantidad: number): string {
  return `${skuLabel} · cantidad ${formatearMiles(cantidad)}`;
}

export function parsearMiles(s: string): number | null {
  const clean = s.replace(/[^\d]/g, '');
  if (clean === '') return null;
  const n = Number(clean);
  return Number.isFinite(n) ? n : null;
}

/**
 * Duración real (minutos) de un bloque de registro, derivada de
 * `hora_inicio_bloque`/`hora_fin_bloque` — NUNCA se asume 60. Bloques
 * parciales de borde de turno (ej. 13:30–14:00) duran 30'. Wrap de
 * medianoche (`hora_fin_bloque <= hora_inicio_bloque`, ej. 23:00–00:00) suma
 * 24h antes de restar.
 */
export function duracionBloqueMin(registro: {
  hora_inicio_bloque: string;
  hora_fin_bloque: string;
}): number {
  const toMin = (h: string) => {
    const [hh, mm, ss = 0] = h.split(':').map(Number);
    return (hh ?? 0) * 60 + (mm ?? 0) + (ss ?? 0) / 60;
  };
  const ini = toMin(registro.hora_inicio_bloque);
  let fin = toMin(registro.hora_fin_bloque);
  if (fin <= ini) fin += 24 * 60;
  return fin - ini;
}

/**
 * % de cumplimiento de UN bloque vs su capacidad estándar. INV-2: la meta se
 * prorratea por la duración REAL del bloque (`cap × duracionMinutos/60`) —
 * NO se asume que todo bloque vale una hora; un parcial de 30' tiene la mitad
 * de meta. Misma semántica que el "% cumplimiento del día" de L2 (real /
 * meta, sin descontar paros). NULL si la combinación SKU × línea no tiene
 * capacidad mapeada en skus_lineas.
 */
export function cumplimientoBloquePct(registro: {
  cantidad_procesada: number;
  capacidad_estandar_unidades_hora: number | null;
  hora_inicio_bloque: string;
  hora_fin_bloque: string;
}): number | null {
  const cap = registro.capacidad_estandar_unidades_hora;
  if (cap === null || cap === 0) return null;
  const metaBloque = cap * (duracionBloqueMin(registro) / 60);
  if (metaBloque === 0) return null;
  return Math.round((Number(registro.cantidad_procesada) / metaBloque) * 100);
}

/**
 * Celda "Cant." de las tablas de bloques (L2 Mis horas / L7 Historial):
 * la cantidad producida con la meta del bloque (prorrateada por su duración
 * real) como subtexto ("de 490" en un parcial de 30' con cap 980/h).
 * `unidad = 'kg'` (SKU a granel) agrega " kg"; en paquetes la celda queda
 * igual que siempre, sin unidad.
 */
export function CantidadConMetaCell({
  registro,
  unidad,
}: {
  unidad?: UnidadMedida;
  registro: {
    cantidad_procesada: number;
    capacidad_estandar_unidades_hora: number | null;
    hora_inicio_bloque: string;
    hora_fin_bloque: string;
  };
}) {
  const cap = registro.capacidad_estandar_unidades_hora;
  const metaBloque = cap !== null && cap > 0 ? cap * (duracionBloqueMin(registro) / 60) : null;
  return (
    <span className="inline-flex flex-col items-end">
      <span className="tabular-nums">
        {formatearMiles(registro.cantidad_procesada)}
        {unidad === 'kg' ? ' kg' : ''}
      </span>
      {metaBloque !== null ? (
        <span className="text-[10px] leading-tight text-fg-tertiary">
          de {formatearMiles(Math.round(metaBloque))}
        </span>
      ) : null}
    </span>
  );
}

/**
 * Celda "Cumpl." — % del bloque con el semáforo unificado 90/70 (el mismo
 * criterio del coordinador). "—" cuando el SKU no tiene capacidad mapeada.
 */
export function CumplimientoBloqueCell({
  registro,
}: {
  registro: {
    cantidad_procesada: number;
    capacidad_estandar_unidades_hora: number | null;
    hora_inicio_bloque: string;
    hora_fin_bloque: string;
  };
}) {
  const pct = cumplimientoBloquePct(registro);
  if (pct === null) {
    return <span className="text-xs text-fg-tertiary">—</span>;
  }
  return (
    <span
      className={`text-xs font-semibold tabular-nums ${toneTextClass(semaforoCumplimiento(pct))}`}
    >
      {pct}%
    </span>
  );
}

// ===========================================================================
// Agrupación por celda (multi-sku-por-hora, Lote 5): "Mis horas" (L2),
// historial (L7) y la agrupación visual del supervisor (S2) reusan estos
// helpers para mostrar 1 card/fila por CELDA del turno en vez de 1 por
// registro — con badge "N SKUs" y sub-filas cuando la celda tiene 2+ tramos.
//
// Un registro histórico SIN partición real (rango completo duplicado, ej. el
// incidente del 22-jul donde 2 SKUs quedaron con el MISMO rango 00:00–01:00)
// cae en el MISMO grupo por celda — se muestra agrupado (no es un bug: son 2
// capturas de la misma hora), y el prorrateo de cumplimiento usa la duración
// PROPIA de cada tramo (nunca la de la celda), así que no asume partición y
// no puede "dividir por cero": si ningún tramo tiene capacidad mapeada, el
// resultado es `null`, nunca `NaN`.
// ===========================================================================

export interface GrupoCelda<T extends { hora_inicio_bloque: string; hora_fin_bloque: string }> {
  celda: { inicio: string; fin: string };
  tramos: T[];
}

/**
 * Agrupa `registros` por la celda del turno (`BloqueTurno`) que contiene cada
 * `hora_inicio_bloque`, vía `bloqueQueContieneHora` (contención por rangos,
 * NUNCA por `HH:00`) — la misma fuente de verdad que el gate del backend
 * (`registros.ts`) y el formulario de captura (Lote 2-4). Si un registro cae
 * fuera de cualquier celda conocida (turno distinto al provisto, dato viejo
 * huérfano), se agrupa solo consigo mismo usando su propio rango como
 * "celda" — nunca se descarta ni rompe.
 *
 * Grupos ordenados por `celda.inicio`; tramos de cada grupo ordenados por
 * `hora_inicio_bloque`. Requiere que TODOS los `registros` pertenezcan al
 * MISMO turno que generó `bloques` — el caller filtra por turno primero
 * (ej. L7 agrupa por fecha+turno antes de llamar acá).
 */
export function agruparPorCelda<T extends { hora_inicio_bloque: string; hora_fin_bloque: string }>(
  registros: T[],
  bloques: BloqueTurno[],
): GrupoCelda<T>[] {
  const grupos = new Map<string, GrupoCelda<T>>();
  for (const r of registros) {
    const celdaResuelta = bloqueQueContieneHora(bloques, String(r.hora_inicio_bloque).slice(0, 5));
    const celda = celdaResuelta
      ? { inicio: celdaResuelta.inicio, fin: celdaResuelta.fin }
      : {
          inicio: String(r.hora_inicio_bloque).slice(0, 5),
          fin: String(r.hora_fin_bloque).slice(0, 5),
        };
    const key = celda.inicio;
    const existente = grupos.get(key);
    if (existente) {
      existente.tramos.push(r);
    } else {
      grupos.set(key, { celda, tramos: [r] });
    }
  }
  const ordenCelda = new Map(bloques.map((b, i) => [b.inicio, i]));
  return Array.from(grupos.values())
    .sort((a, b) => {
      const ia = ordenCelda.get(a.celda.inicio);
      const ib = ordenCelda.get(b.celda.inicio);
      if (ia !== undefined && ib !== undefined) return ia - ib;
      if (ia !== undefined) return -1;
      if (ib !== undefined) return 1;
      return a.celda.inicio.localeCompare(b.celda.inicio);
    })
    .map((g) => ({
      ...g,
      tramos: [...g.tramos].sort((a, b) =>
        String(a.hora_inicio_bloque).localeCompare(String(b.hora_inicio_bloque)),
      ),
    }));
}

/**
 * Cumplimiento % de un GRUPO de tramos (misma celda): Σ cantidad real sobre
 * Σ meta prorrateada por la duración REAL de cada tramo (INV-2, la misma
 * fórmula que `cumplimientoBloquePct` aplicada a un solo registro — con 1
 * tramo el resultado es idéntico, sin regresión). Ignora tramos sin
 * capacidad mapeada; `null` si NINGÚN tramo del grupo tiene capacidad
 * (nunca `NaN` por dividir sobre meta 0).
 */
export function cumplimientoGrupoPct(
  tramos: {
    cantidad_procesada: number;
    capacidad_estandar_unidades_hora: number | null;
    hora_inicio_bloque: string;
    hora_fin_bloque: string;
  }[],
): number | null {
  let real = 0;
  let meta = 0;
  for (const r of tramos) {
    const cap = r.capacidad_estandar_unidades_hora;
    if (cap === null || cap === 0) continue;
    real += Number(r.cantidad_procesada);
    meta += cap * (duracionBloqueMin(r) / 60);
  }
  if (meta === 0) return null;
  return Math.round((real / meta) * 100);
}

/** Σ `cantidad_procesada` de todos los tramos de un grupo (celda para "Cant."). */
export function cantidadTotalGrupo(tramos: { cantidad_procesada: number }[]): number {
  return tramos.reduce((acc, r) => acc + Number(r.cantidad_procesada), 0);
}

/** Cantidad real de SKUs; un mismo SKU puede aparecer en tramos disjuntos. */
export function cantidadSkusDistintos(tramos: { sku_id: string }[]): number {
  return new Set(tramos.map((t) => t.sku_id)).size;
}

/**
 * CantidadInput — input con separador de miles es-CO para cantidades grandes.
 * Mantiene el `value` real (number) en el form via prop callback. El display
 * formatea con `Intl.NumberFormat`; el `onChange` parsea quitando puntos.
 */
export function CantidadInput({
  id,
  label,
  placeholder,
  value,
  onChange,
  error,
}: {
  id: string;
  label: string;
  placeholder?: string;
  value: number | null;
  onChange: (v: number | null) => void;
  error?: string;
}) {
  const [focused, setFocused] = useState(false);
  const display = focused
    ? value === null
      ? ''
      : String(value)
    : value === null
      ? ''
      : formatearMiles(value);

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder={placeholder}
        aria-invalid={Boolean(error) || undefined}
        value={display}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={(e) => onChange(parsearMiles(e.target.value))}
      />
      {error ? <p className="text-xs text-crit">{error}</p> : null}
    </div>
  );
}
